import { request } from "./api";
import { ATTENDANCE_TIMING_PREFETCH_ROLES } from "../constants";

const CACHE_MAX_AGE_MS = 30 * 1000; // 30 seconds — module cache for instant read
const SESSION_CACHE_KEY = "attendanceTimingCache";
const SESSION_CACHE_MAX_AGE_MS = 10 * 60 * 1000; // 10 min for sessionStorage

let moduleCache = null;
let inFlightPromise = null;

function getCurrentUserRoleKey() {
  try {
    const raw = localStorage.getItem("currentUser");
    if (!raw) return null;
    const user = JSON.parse(raw);
    const r = user?.role;
    if (r == null) return null;
    if (typeof r === "string") return r.trim();
    const key = r.name ?? r.code;
    return key != null ? String(key).trim() : null;
  } catch {
    return null;
  }
}

export function isAttendanceTimingPrefetchRole() {
  const key = getCurrentUserRoleKey();
  if (!key) return false;
  const lower = key.toLowerCase();
  return ATTENDANCE_TIMING_PREFETCH_ROLES.some((a) => a.toLowerCase() === lower);
}

function getModuleCache() {
  if (!moduleCache) return null;
  const age = Date.now() - (moduleCache.cachedAt || 0);
  if (age > CACHE_MAX_AGE_MS || age < 0) return null;
  return moduleCache;
}

export function getCachedTiming() {
  if (!isAttendanceTimingPrefetchRole()) return null;

  const fromModule = getModuleCache();
  if (fromModule) {
    return {
      result: fromModule.result,
      lastTimingFetchAt: fromModule.lastTimingFetchAt,
      cachedAt: fromModule.cachedAt
    };
  }

  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const { result, lastTimingFetchAt, cachedAt } = data;
    if (!result || !lastTimingFetchAt) return null;
    const now = Date.now();
    const age = cachedAt != null ? now - cachedAt : now - new Date(lastTimingFetchAt).getTime();
    if (age > SESSION_CACHE_MAX_AGE_MS || age < 0) return null;
    return { result, lastTimingFetchAt, cachedAt };
  } catch {
    return null;
  }
}

export function setCachedTiming(response) {
  if (!response || response.status !== 1 || !response.result) return;
  const lastTimingFetchAt = response.time_stamp || new Date().toISOString();
  const cached = {
    result: response.result,
    lastTimingFetchAt,
    cachedAt: Date.now()
  };
  moduleCache = cached;
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cached));
  } catch (_) {}
}

export function prefetchTiming() {
  if (!isAttendanceTimingPrefetchRole()) {
    return Promise.resolve(null);
  }
  if (inFlightPromise) return inFlightPromise;
  inFlightPromise = request("/attendance/current-timing")
    .then((response) => {
      setCachedTiming(response);
      return response;
    })
    .finally(() => {
      inFlightPromise = null;
    });
  return inFlightPromise;
}

export function clearTimingCache() {
  moduleCache = null;
  inFlightPromise = null;
  try {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
  } catch (_) {}
}
